import { type MouseEvent, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { type JSX } from 'react/jsx-runtime';
import { FormattedDate, FormattedMessage } from 'react-intl';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CursorIcon from '@atlaskit/icon-lab/core/cursor';
import AutomationIcon from '@atlaskit/icon/core/automation';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import Image from '@atlaskit/image/image';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { useModal } from '@atlaskit/modal-dialog/hooks';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog/types';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { ANALYTICS_CHANNEL, useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import illustration from './assets/illustration.png';
import illustrationDarkMode from './assets/illustration-dark.png';

export interface PromoModalProps {
	isOpen: boolean;
	onClose: () => void;
	productName: string;
	edition: string;
	placeOrderHref: string;
	modalName: string;
}

/** When the discount offer expires. Month is 0-indexed, so this is November 8, 2026. */
const OFFER_EXPIRY_DATE = new Date(2026, 10, 8);

const styles = cssMap({
	root: {
		// Fill the dialog above the footer so the footer stays pinned when the viewport caps the height.
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: 0,
	},
	scrollContainer: {
		// Scroll if content is taller than the viewport allows (e.g. small screens or zoomed text).
		flexGrow: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	hero: {
		display: 'flex',
		height: '240px',
		// Match the modal's radius.xlarge so the illustration follows the dialog's rounded corners.
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	closeButton: {
		position: 'absolute',
		// Matches ModalHeader's padding, which previously positioned the close button.
		top: token('space.300'),
		right: token('space.300'),
		zIndex: 1,
	},
	content: {
		paddingBlockStart: token('space.300'),
		// Combined with ModalFooter's 16px top padding, this gives 32px between the expiry and the CTAs.
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	feature: {
		gap: token('space.150'),
		alignItems: 'center',
	},
});

const JIRA_PRODUCT_DISCOVERY = 'Jira Product Discovery';

const getFeatures = (productName: string) => [
	{
		id: 'pickUp',
		Icon: CursorIcon,
		message: (
			<FormattedMessage
				id="growth-transmuters.recent-downgrade-discount.modal.feature.pick-up"
				defaultMessage="Instantly pick up where you left off"
				description="Feature bullet in the recent downgrade discount modal explaining the user can resume their previous setup"
			/>
		),
	},
	{
		id: 'limits',
		Icon: AutomationIcon,
		message:
			productName === JIRA_PRODUCT_DISCOVERY ? (
				<FormattedMessage
					id="growth-transmuters.recent-downgrade-discount.modal.feature.automation-steps-per-user"
					defaultMessage="Get more automation steps per user"
					description="Feature bullet in the recent downgrade discount modal for Jira Product Discovery, describing a higher automation step limit per user"
				/>
			) : (
				<FormattedMessage
					id="growth-transmuters.recent-downgrade-discount.modal.feature.automation-steps"
					defaultMessage="Get more automation steps and AI credits"
					description="Feature bullet in the recent downgrade discount modal describing higher automation step and AI credit limits"
				/>
			),
	},
	{
		id: 'cancel',
		Icon: CheckMarkIcon,
		message: (
			<FormattedMessage
				id="growth-transmuters.recent-downgrade-discount.modal.feature.cancel"
				defaultMessage="Cancel or switch back anytime"
				description="Feature bullet in the recent downgrade discount modal reassuring the user they can cancel or switch back"
			/>
		),
	},
];

/**
 * The modal's close button, shown over the illustration. Uses the modal's own `onClose` (the
 * same one `ModalHeader` would use) so the close analytics event is created by modal-dialog.
 */
const PromoModalCloseButton = (): JSX.Element => {
	const { onClose, testId } = useModal();
	return <CloseButton onClick={onClose} testId={testId} />;
};

/** The visible heading, used as the modal's accessible title via `titleId` from `useModal()`. */
const PromoModalTitle = ({ children }: { children: ReactNode }): JSX.Element => {
	const { titleId } = useModal();
	return (
		<Heading size="large" as="h1" id={titleId}>
			{children}
		</Heading>
	);
};

export const PromoModal = ({
	isOpen,
	onClose,
	productName,
	edition,
	placeOrderHref,
	modalName,
}: PromoModalProps): JSX.Element => {
	const hasFiredViewedRef = useRef(false);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onCtaActioned, onDeclined } = usePostOfficeUserInteractions();

	useEffect(() => {
		if (!isOpen || hasFiredViewedRef.current) return;

		hasFiredViewedRef.current = true;
		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: modalName,
			attributes: {
				team: 'growth-transmuters',
				productName,
				edition,
			},
		});
	}, [isOpen, messageInstanceViewed, fireAnalyticsEvent, productName, edition, modalName]);

	/**
	 * Fires a `ui button clicked` event on the Post Office channel. Reuses the event created by
	 * the ADS component so its context and attributes are kept, adding our actionSubjectId and
	 * the same attributes as the modal's screen event.
	 */
	const fireButtonClicked = useCallback(
		(analyticsEvent: UIAnalyticsEvent, actionSubjectId: string) => {
			analyticsEvent
				.update((payload) => ({
					...payload,
					eventType: 'ui',
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId,
					attributes: {
						...payload.attributes,
						team: 'growth-transmuters',
						productName,
						edition,
					},
				}))
				.fire(ANALYTICS_CHANNEL);
		},
		[productName, edition],
	);

	const handleOnClose = useCallback(
		(_e: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireButtonClicked(analyticsEvent, `${modalName}Close`);
			onClose();
		},
		[fireButtonClicked, onClose, modalName],
	);

	const handleNoThanks = useCallback(
		(_e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireButtonClicked(analyticsEvent, `${modalName}NoThanks`);
			onDeclined({ interactionIdentifier: 'noThanks' });
			onClose();
		},
		[fireButtonClicked, onClose, onDeclined, modalName],
	);

	const handleClaimOffer = useCallback(
		(_e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			// The LinkButton's native navigation opens placeOrderHref in a new tab.
			fireButtonClicked(analyticsEvent, `${modalName}ClaimOffer`);
			onCtaActioned({ interactionIdentifier: 'claimOffer' });
		},
		[fireButtonClicked, onCtaActioned, modalName],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					// "medium" is 600px wide. Height grows to fit the content.
					width="medium"
					onClose={handleOnClose}
					testId="recent-downgrade-discount-modal"
				>
					<Box xcss={styles.root}>
						<Box xcss={styles.closeButton}>
							<PromoModalCloseButton />
						</Box>

						<Box xcss={styles.scrollContainer}>
							<Box xcss={styles.hero}>
								<Image
									src={illustration}
									srcDark={illustrationDarkMode}
									alt=""
									width={1200}
									height={480}
									testId="recent-downgrade-discount-modal-illustration"
								/>
							</Box>

							<Stack space="space.300" xcss={styles.content}>
								<Stack space="space.150">
									<PromoModalTitle>
										<FormattedMessage
											id="growth-transmuters.recent-downgrade-discount.modal.heading"
											defaultMessage="Get 20% off your next 3 months"
											description="Heading of the recent downgrade discount modal"
										/>
									</PromoModalTitle>
									<Text>
										<FormattedMessage
											id="growth-transmuters.recent-downgrade-discount.modal.body"
											defaultMessage="Restore your {productName} {edition} plan today and get 20% off your bill for 3 months.<sup>*</sup>"
											description="Body text of the recent downgrade discount modal. {productName} is the product (e.g. Jira) and {edition} is the edition the user downgraded from (e.g. Premium)"
											values={{
												productName,
												edition,
												sup: (chunks) => <sup>{chunks}</sup>,
											}}
										/>
									</Text>
								</Stack>

								<Stack space="space.200">
									{getFeatures(productName).map(({ id, Icon, message }) => (
										<Flex key={id} xcss={styles.feature}>
											<Icon label="" color={token('color.icon.accent.blue')} />
											<Text>{message}</Text>
										</Flex>
									))}
								</Stack>

								<Text size="small" color="color.text.subtlest">
									<FormattedMessage
										id="growth-transmuters.recent-downgrade-discount.modal.footnote"
										defaultMessage="<sup>*</sup>Offer expires on {expiryDate}"
										description="Footnote in the recent downgrade discount modal showing when the offer expires. {expiryDate} is a formatted date, e.g. November 1, 2026"
										values={{
											expiryDate: (
												<FormattedDate
													value={OFFER_EXPIRY_DATE}
													year="numeric"
													month="long"
													day="numeric"
												/>
											),
											sup: (chunks) => <sup>{chunks}</sup>,
										}}
									/>
								</Text>
							</Stack>
						</Box>
					</Box>

					<ModalFooter>
						<Button
							appearance="default"
							onClick={handleNoThanks}
							testId="recent-downgrade-discount-modal-no-thanks"
						>
							<FormattedMessage
								id="growth-transmuters.recent-downgrade-discount.modal.secondary-action"
								defaultMessage="No thanks"
								description="Secondary button in the recent downgrade discount modal to decline the offer"
							/>
						</Button>
						<LinkButton
							appearance="primary"
							href={placeOrderHref}
							target="_blank"
							onClick={handleClaimOffer}
							testId="recent-downgrade-discount-modal-claim-offer"
						>
							<FormattedMessage
								id="growth-transmuters.recent-downgrade-discount.modal.primary-action"
								defaultMessage="Claim offer"
								description="Primary button in the recent downgrade discount modal that opens the discounted order page in a new tab"
							/>
						</LinkButton>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
